import _ from 'lodash';
import { type ExperienceMultiRowSurveyCard } from '@bbkAdminRedux/rtkq/experience.slice';
import { type RestCard } from '@bbkAdminUtils/api-client/rest-experiences-api';
import {
  RestLossAversionCardTemplates,
  type RestLossAversionCard,
  type RestLossAversionWithOffer,
} from '@bbkAdminUtils/api-client/rest-la-cards-types';
import {
  RestModalTemplates,
  type RestModalCard,
  type RestModalFinalOffer,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import {
  type NewOffer,
  type RestLossAversion,
  type RestOffer,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { type EnumLike } from '@bbkAdminUtils/utility-types';

type ModelGenerated = {
  offer_content_id?: number;
};

export type RestAbstractCard = {
  id: string;
  name: string;
  version: string;
  type: 'Card';
  deploy: string;
  order?: number;
} & ModelGenerated;

export type RestLossAversionCardOrModalCard =
  RestModalCard | RestLossAversionCard;

export const isLossAversionCardWithOffer = (
  card: RestCard
): card is RestLossAversionWithOffer => {
  return (
    card.template === RestLossAversionCardTemplates.simple_list ||
    card.template === RestLossAversionCardTemplates.loss_simple_link ||
    card.template === RestLossAversionCardTemplates.la_one_up_image
  );
};

export const isLossAversionCard = (
  card: RestCard
): card is RestLossAversionCard => {
  return (
    card.template === RestLossAversionCardTemplates.simple_chip ||
    card.template === RestLossAversionCardTemplates.simple_chip_images ||
    card.template === RestLossAversionCardTemplates.simple_team ||
    card.template === RestLossAversionCardTemplates.simple_list ||
    card.template === RestLossAversionCardTemplates.loss_simple_link ||
    card.template === RestLossAversionCardTemplates.simple_blurb ||
    card.template === RestLossAversionCardTemplates.la_one_up_image ||
    card.template === RestLossAversionCardTemplates.la_twitter_card
  );
};

export const isModalCard = (card: RestCard): card is RestModalCard => {
  return Object.keys(RestModalTemplates).includes(card.template);
};

export const isModalTemplate = (
  template:
    | EnumLike<typeof RestModalTemplates>
    | EnumLike<typeof RestLossAversionCardTemplates>
): template is RestModalTemplates => {
  return Object.keys(RestModalTemplates).includes(template);
};

export const isFinalOfferCard = (
  card: RestCard
): card is RestModalFinalOffer => {
  return RestModalTemplates.final_offer === card.template;
};

export const findPrimaryCard = (
  offerOrLossAversion: RestOffer | RestLossAversion
): RestLossAversionCardOrModalCard => {
  const pcName = offerOrLossAversion.primary_card_name;
  return (offerOrLossAversion.cards as RestLossAversionCardOrModalCard[]).find(
    (c) => c.name === pcName
  )!;
};

export const isLossAversion = (
  offerOrLossAversion: RestOffer | RestLossAversion
): offerOrLossAversion is RestLossAversion => {
  const primaryCard = findPrimaryCard(offerOrLossAversion);
  if (!primaryCard) {
    GlobalLogger.error(`Could not find primary card ${primaryCard}`);
    return false;
  }
  return isLossAversionCard(primaryCard);
};

export const isOfferOrLaWithOffer = (
  offerOrLossAversion: RestOffer | RestLossAversion
): offerOrLossAversion is RestOffer => {
  const primaryCard = findPrimaryCard(offerOrLossAversion);
  if (!primaryCard) {
    GlobalLogger.error(`Could not find primary card ${primaryCard}`);
    return false;
  }
  return isModalCard(primaryCard) || isLossAversionCardWithOffer(primaryCard);
};

export const isOffer = (
  offerOrLossAversion: RestOffer | RestLossAversion
): offerOrLossAversion is RestOffer => {
  const primaryCard = findPrimaryCard(offerOrLossAversion);
  if (!primaryCard) {
    GlobalLogger.error(`Could not find primary card ${primaryCard}`);
    return false;
  }
  return isModalCard(primaryCard);
};

export const isOfferCardPack = (offer: RestOffer | NewOffer) => {
  if (!offer) return false;
  const primaryCardIndex = offer.cards.findIndex(
    (c) => c.name === offer.primary_card_name
  );
  if (primaryCardIndex < 0) return false;
  const cardPackPath = ['cards', primaryCardIndex, 'requires', 'content_set'];
  return !!_.get(offer, cardPackPath);
};

export const isReasonOffer = (
  offerOrLossAversion: RestOffer | RestLossAversion
): offerOrLossAversion is RestOffer => {
  const card = offerOrLossAversion.cards[0];
  return (
    isModalCard(card as RestModalCard) &&
    !isFinalOfferCard(card as RestModalFinalOffer)
  );
};

export const isFinalOffer = (offer: RestOffer): offer is RestOffer => {
  return isFinalOfferCard(offer.cards[0] as RestModalCard);
};

export enum SurveyType {
  multi_row_survey = 'multi_row_survey',
  multi_tier_survey = 'multi_tier_survey',
}

export type RestMultiRowSurveyCard = ExperienceMultiRowSurveyCard;
